import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Banknote, CalendarRange, Users } from 'lucide-react';
import { BatchStageBadge } from '@/components/common/BatchStageBadge';
import { DetailTableLayout } from '@/components/common/DetailTableLayout';
import { ExportButton } from '@/components/common/ExportButton';
import { LabeledPillList, type PillField } from '@/components/common/LabeledPill';
import { EMPTY_VALUE, TAKA_SYMBOL } from '@/constants/app.constants';
import { PUBLISH_PERMISSIONS } from '@/constants/permissions';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { useRememberViewedRow } from '@/hooks/useLastViewedRow';
import { Button } from '@/components/ui/button';
import { formatDisplayDate } from '@/utils/date';
import { filterRowsBySearch, getTableEmptyText } from '@/utils/listSearch';
import {
  batchMembersQueryOptions,
  batchQueryOptions,
  downloadBatchBills,
  exportBatchMembers,
  openMemberBill,
} from '../api/publish.api';
import { BatchActionDialog } from '../components/BatchActionDialog';
import { getBatchMemberColumns } from '../components/BatchColumns';
import { BatchTrail } from '../components/BatchTrail';
import { useDisburseBatch } from '../hooks/usePublishMutations';
import type { Batch, BatchMembersSearch } from '../types/publish.types';
import { formatBatchAmount } from '../utils/formatBatchAmount';

const ARCHIVED_MEMBERS_MESSAGE = 'Member details are not kept for this batch';
const ARCHIVED_MEMBERS_HINT = 'This batch is too old. Only the totals above are kept.';

function formatCovering(batch: Batch): string {
  if (batch.trnDateFrom === null) return EMPTY_VALUE;
  return `${formatDisplayDate(batch.trnDateFrom)} – ${formatDisplayDate(batch.trnDateTo)}`;
}

function getBatchPills(batch: Batch | undefined, isLoading: boolean): PillField[] {
  return [
    {
      key: 'members',
      icon: Users,
      label: 'ZMs',
      value: batch === undefined ? undefined : formatBatchAmount(batch.memberCount),
      isLoading,
    },
    {
      key: 'total',
      icon: Banknote,
      label: 'Total',
      value:
        batch === undefined ? undefined : `${TAKA_SYMBOL} ${formatBatchAmount(batch.totalAmount)}`,
      isLoading,
    },
    {
      key: 'covering',
      icon: CalendarRange,
      label: 'Covering',
      value: batch === undefined ? undefined : formatCovering(batch),
      isLoading,
    },
  ];
}

interface BatchMembersPageProps {
  batchId: number;
  search: BatchMembersSearch;
}

export function BatchMembersPage({ batchId, search }: BatchMembersPageProps) {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [isDisbursing, setIsDisbursing] = useState(false);

  useRememberViewedRow('publishBatches', batchId);

  const batchQuery = useQuery(batchQueryOptions(batchId));
  const membersQuery = useQuery(batchMembersQueryOptions(batchId));
  const batch = batchQuery.data ?? undefined;
  const isArchived = batch?.rowsArchived === true;
  const allMembers = useMemo(() => membersQuery.data ?? [], [membersQuery.data]);
  const [memberSearch, setMemberSearch] = useState('');

  const members = useMemo(
    () =>
      filterRowsBySearch(allMembers, memberSearch, (member) => [
        member.fullName,
        member.hrEmpId,
        member.mobile,
        member.username,
        member.departmentName,
        member.divisionName,
        member.districtName,
        member.thanaName,
      ]),
    [allMembers, memberSearch],
  );

  const columns = useMemo(
    () =>
      getBatchMemberColumns(async (member) => {
        await openMemberBill(batchId, member.userId);
      }, members),
    [batchId, members],
  );

  const disburse = useDisburseBatch(() => setIsDisbursing(false));

  const canDisburse =
    batch?.isConfirm === true &&
    !batch.isDisburse &&
    !batch.rowsArchived &&
    can(PUBLISH_PERMISSIONS.ACCOUNTS_DISBURSE);

  return (
    <>
      <DetailTableLayout
        title={batch === undefined ? undefined : `Batch #${String(batch.legacyId)}`}
        titleAddon={batch === undefined ? null : <BatchStageBadge stage={batch.stage} />}
        actions={
          <>
            {canDisburse ? (
              <Button type="button" onClick={() => setIsDisbursing(true)}>
                Disburse
              </Button>
            ) : null}
            <ExportButton
              onExport={() => downloadBatchBills(batchId, batch?.legacyId ?? batchId)}
              label="All bills"
              disabled={members.length === 0}
            />
            <ExportButton
              onExport={() => exportBatchMembers(batchId, batch?.legacyId ?? batchId)}
              disabled={members.length === 0}
            />
          </>
        }
        search={{
          value: memberSearch,
          onChange: setMemberSearch,
          placeholder: 'ZM name, ID or mobile',
        }}
        onNoHistory={() => void navigate({ to: search.from })}
        summary={
          <>
            <LabeledPillList fields={getBatchPills(batch, batchQuery.isLoading)} />
            <BatchTrail batch={batch} />
          </>
        }
        table={{
          rows: members,
          columns,
          isLoading: membersQuery.isLoading,
          error: membersQuery.error ?? batchQuery.error,
          errorTitle: 'Could not load the ZMs on this batch',
          onRetry: () => {
            void batchQuery.refetch();
            void membersQuery.refetch();
          },
          stickyColumnId: 'member',
          ...(isArchived
            ? { emptyMessage: ARCHIVED_MEMBERS_MESSAGE, emptyHint: ARCHIVED_MEMBERS_HINT }
            : getTableEmptyText(memberSearch.trim(), 'No ZM is on this batch')),
        }}
      />

      <BatchActionDialog
        batch={isDisbursing && batch !== undefined ? batch : null}
        title="Disburse this batch?"
        description="Accounts records the payment against this batch. The DCRs in it stay as they are."
        confirmLabel="Disburse"
        commentRequired
        isPending={disburse.isPending}
        onConfirm={(comments) => {
          disburse.mutate({ batchId, comments });
        }}
        onOpenChange={(open) => {
          if (!open) setIsDisbursing(false);
        }}
      />
    </>
  );
}
